import { useMemo } from 'react'
import useRetailerApi from '@/useRetailerApi'

// En dessous de 2 caractères, à peu près tout le catalogue matche.
const MIN_QUERY_LENGTH = 2
// Un préfixe de SKU plus court ramènerait des familles entières (AQU = 126 références).
const MIN_SKU_PREFIX_LENGTH = 3
// La distance 1 n'est discriminante que sur des mots assez longs.
const MIN_FUZZY_LENGTH = 5

const RANK = {
    SKU_EXACT: 5,
    SKU_PREFIX: 4,
    NAME_WORD: 3,
    NAME_CONTAINS: 2,
    NAME_FUZZY: 1,
}

// "Hermétique" -> "hermetique"
const normalize = (value) =>
    (value ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .trim()

// Les SKU n'ont ni espace ni séparateur : "aqu 11" et "AQU11" sont la même saisie.
const normalizeSku = (value) => normalize(value).replace(/\s+/g, '')

// On ne cherche pas la distance de Levenshtein exacte, seulement à savoir si un
// unique ajout / retrait / remplacement suffit à passer de a à b.
function isWithinOneEdit(a, b) {
    if (a === b) return true
    if (Math.abs(a.length - b.length) > 1) return false

    let i = 0
    let j = 0
    let edited = false

    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) {
            i++
            j++
            continue
        }

        if (edited) return false
        edited = true

        if (a.length > b.length) i++
        else if (a.length < b.length) j++
        else {
            i++
            j++
        }
    }

    return true
}

// Un terme est satisfait dès qu'il préfixe un mot du titre. Découper la saisie
// comme on découpe les titres est ce qui rend l'ordre indifférent : "bronze
// black" et "black bronze" tombent sur les mêmes mots.
const matchesAllWords = (row, terms) =>
    terms.every((term) => row.words.some((word) => word.startsWith(term)))

// Repli : le terme n'est pas en début de mot mais bien présent quelque part.
const matchesAllInTitle = (row, terms) => terms.every((term) => row.title.includes(term))

/**
 * Même exigence, un cran plus permissif : chaque terme doit préfixer un mot OU
 * n'en différer que d'une faute. Relâcher terme par terme et non sur la saisie
 * entière est ce qui permet de rattraper "bronze blck" — le flou d'origine
 * comparait "bronze blck" à des mots isolés, il ne pouvait jamais aboutir.
 *
 * Le seuil reste celui du mot isolé : sous MIN_FUZZY_LENGTH, une faute rapproche
 * trop de références pour discriminer.
 */
const matchesAllFuzzy = (row, terms) =>
    terms.every((term) =>
        row.words.some(
            (word) =>
                word.startsWith(term) ||
                (term.length >= MIN_FUZZY_LENGTH &&
                    word.length >= MIN_FUZZY_LENGTH &&
                    isWithinOneEdit(word, term))
        )
    )

function matchRow(row, terms, skuQuery) {
    // SKU : exact, puis préfixe, jamais de tolérance aux fautes — 86 % des SKU du
    // catalogue ne diffèrent d'un autre que par leur dernier caractère.
    const exact = row.variants.find((variant) => variant.sku && variant.sku === skuQuery)
    if (exact) return { rank: RANK.SKU_EXACT, variantId: exact.id }

    if (skuQuery.length >= MIN_SKU_PREFIX_LENGTH) {
        const prefixed = row.variants.filter(
            (variant) => variant.sku && variant.sku.startsWith(skuQuery)
        )

        if (prefixed.length) {
            // Saisie partielle : aucune variante n'est vraiment ciblée, autant
            // ouvrir la modale sur une disponible.
            const target =
                prefixed.find((variant) => variant.stockStatus === 'available') ??
                prefixed.find((variant) => variant.stockStatus === 'low') ??
                prefixed[0]

            return { rank: RANK.SKU_PREFIX, variantId: target.id }
        }
    }

    if (matchesAllWords(row, terms)) {
        return { rank: RANK.NAME_WORD, variantId: null }
    }

    if (matchesAllInTitle(row, terms)) {
        return { rank: RANK.NAME_CONTAINS, variantId: null }
    }

    return { rank: 0, variantId: null }
}

/**
 * Recherche par nom de produit ou par SKU dans le catalogue déjà chargé par
 * `/me` — aucune requête supplémentaire.
 *
 * Les résultats sont toujours au niveau produit : `variantId` porte la précision
 * du SKU et sert à ouvrir `ProductModal` directement sur la bonne variante.
 */
export default function useProductSearch(query) {
    const { retailer } = useRetailerApi()

    const index = useMemo(() => {
        const rows = []
        const seen = new Set()

        for (const collection of retailer?.collections ?? []) {
            for (const product of collection.products) {
                // Un produit peut appartenir à plusieurs collections : une seule carte.
                if (seen.has(product.id)) continue
                seen.add(product.id)

                const title = normalize(product.title)

                rows.push({
                    product,
                    collection,
                    title,
                    words: title.split(/[^a-z0-9]+/).filter(Boolean),
                    variants: product.variants.map((variant) => ({
                        id: variant.id,
                        sku: normalizeSku(variant.sku),
                        stockStatus: variant.stock_status,
                    })),
                })
            }
        }

        return rows
    }, [retailer?.collections])

    return useMemo(() => {
        const normalizedQuery = normalize(query)

        if (normalizedQuery.length < MIN_QUERY_LENGTH) {
            return { isSearching: false, results: [] }
        }

        // Découpé comme les titres le sont : la saisie devient une liste de termes
        // tous exigés, dans n'importe quel ordre.
        const terms = normalizedQuery.split(/[^a-z0-9]+/).filter(Boolean)

        if (terms.length === 0) {
            return { isSearching: false, results: [] }
        }

        const skuQuery = normalizeSku(query)
        const matches = []

        for (const row of index) {
            const { rank, variantId } = matchRow(row, terms, skuQuery)
            if (rank) matches.push({ row, rank, variantId })
        }

        // La tolérance aux fautes n'intervient qu'en dernier recours : elle ne peut
        // donc jamais polluer une recherche qui donnait déjà des résultats. Inutile
        // de la tenter si aucun terme n'est assez long pour qu'une faute discrimine.
        if (matches.length === 0 && terms.some((term) => term.length >= MIN_FUZZY_LENGTH)) {
            for (const row of index) {
                if (matchesAllFuzzy(row, terms)) {
                    matches.push({ row, rank: RANK.NAME_FUZZY, variantId: null })
                }
            }
        }

        matches.sort((a, b) => b.rank - a.rank || a.row.title.localeCompare(b.row.title))

        return {
            isSearching: true,
            results: matches.map(({ row, variantId }) => ({
                product: row.product,
                collection: row.collection,
                variantId,
            })),
        }
    }, [index, query])
}
